import { DraggableProvidedDragHandleProps } from '@atlaskit/pragmatic-drag-and-drop-react-beautiful-dnd-migration'
import { GripVerticalIcon, Trash2Icon } from 'lucide-react'
import { forwardRef, useCallback, useMemo, useState } from 'react'
import {
  Control,
  FieldError,
  FieldErrors,
  FieldValues,
  useFormContext,
  UseFormSetValue,
  useFormState,
  useWatch,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import useSWR from 'swr'

import { skuTaxRate, skuUnitPrice } from '@/components/CashRegister/registerUtils'
import { Money } from '@/components/ds'
import { Button } from '@/components/ui/button'
import {
  Combobox,
  ComboboxContent,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from '@/components/ui/combobox'
import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { apiFetcher } from '@/data/fetcher'
import { DocumentFormValues } from '@/forms/DocumentForm'
import { Product } from '@/interfaces/catalog/Product'
import { Sku } from '@/interfaces/catalog/Sku'
import { formatProductSkuLabel } from '@/lib/catalog/skuUtils'
import { cn } from '@/lib/utils'

import { CustomNumberInput } from '../components/CustomNumberInput'
import { TaxSelect } from '../components/TaxSelect'
import { toNumber } from '../DocumentForm/calculateTotals'
import { itemGridClass } from './gridTemplate'

interface SkuOption {
  key: string
  label: string
  product: Product
  sku?: Sku
}

export interface DocumentItemProps {
  control: Control<DocumentFormValues>
  setValue: UseFormSetValue<DocumentFormValues>
  products: Product[]
  currencySymbol: string
  currencyCode?: string
  field: FieldValues
  index: number
  hasRemoveButton?: boolean
  dragHandleProps?: DraggableProvidedDragHandleProps | undefined | null
  isDragDisabled?: boolean
  onRemoveClick?: () => void
  onLastInputEnterKeyDown?: () => void
  isVatPayer: boolean
}

export const DocumentItem = forwardRef(
  (
    {
      control,
      setValue,
      products,
      currencySymbol,
      currencyCode = 'CZK',
      field,
      index,
      hasRemoveButton,
      dragHandleProps,
      isDragDisabled,
      onRemoveClick,
      onLastInputEnterKeyDown,
      isVatPayer,
      ...rest
    }: DocumentItemProps,
    ref: any,
  ) => {
    const { t } = useTranslation()
    const { errors: formErrors } = useFormState({
      control,
      name: `items.${index}`,
    })
    const rowErrors = (
      formErrors.items as FieldErrors<DocumentFormValues['items']> | undefined
    )?.[index]
    const unitPriceError = rowErrors?.unitPrice as FieldError | undefined

    const methods = useFormContext<DocumentFormValues>()
    const {
      register,
      watch,
    } = methods
    const [confirmRemove, setConfirmRemove] = useState(false)
    const [comboboxOpen, setComboboxOpen] = useState(false)

    const selectedSkuId = watch(`items.${index}.skuId`)
    const [lineAmount, lineUnitPrice] = useWatch({
      control,
      name: [`items.${index}.amount`, `items.${index}.unitPrice`],
    })
    const lineTotal = toNumber(lineAmount) * toNumber(lineUnitPrice)

    const { data: productsResponse } = useSWR<{ data?: Product[]; total?: number }>(
      '/api/v2/products',
      apiFetcher,
    )

    const { data: skusResponse } = useSWR<{ data?: Sku[]; total?: number }>(
      '/api/v2/skus',
      apiFetcher,
    )

    const skuOptions = useMemo<SkuOption[]>(() => {
      const skusByProduct = new Map<string, Sku[]>()
      for (const sku of skusResponse?.data ?? []) {
        const list = skusByProduct.get(sku.productId) ?? []
        list.push(sku)
        skusByProduct.set(sku.productId, list)
      }

      const options: SkuOption[] = []
      for (const product of productsResponse?.data ?? []) {
        const productSkus = skusByProduct.get(product.id) ?? []
        if (productSkus.length > 1) {
          for (const sku of productSkus) {
            options.push({
              key: sku.id,
              label: formatProductSkuLabel(product.name, sku.name),
              product,
              sku,
            })
          }
        } else {
          options.push({
            key: product.id,
            label: product.name,
            product,
            sku: productSkus[0],
          })
        }
      }
      return options
    }, [productsResponse?.data, skusResponse?.data])

    const applyOption = useCallback(
      (option: SkuOption) => {
        const { sku } = option
        setValue(`items.${index}.description`, option.label)
        setValue(`items.${index}.skuId`, sku?.id ?? null)
        if (sku) {
          setValue(`items.${index}.unitPrice`, skuUnitPrice(sku))
          if (isVatPayer) {
            const taxRate = skuTaxRate(sku)
            if (taxRate != null) {
              setValue(`items.${index}.taxRate`, taxRate)
            }
          }
        }
      },
      [index, isVatPayer, setValue],
    )

    return (
      <div
        ref={ref}
        className={cn(itemGridClass(isVatPayer), 'group/item relative py-1')}
        {...rest}
      >
        <div
          aria-label="Přesunout"
          className={cn(
            'absolute top-1 -left-5 grid h-8 w-4 place-content-center place-items-center rounded-sm text-muted-foreground transition-opacity',
            {
              'cursor-grab opacity-0 group-hover/item:opacity-100 hover:bg-muted focus-visible:opacity-100': !isDragDisabled,
              'pointer-events-none opacity-0': isDragDisabled,
            },
          )}
          {...dragHandleProps}
        >
          <GripVerticalIcon className="size-4" />
        </div>

        <div className="min-w-0">
          <FormField
            control={control}
            name={`items.${index}.description`}
            render={({ field }) => {
              const selectedOption =
                skuOptions.find((o) => o.sku?.id === selectedSkuId) ?? null
              const inputLength = (field.value ?? '').trim().length
              const open = inputLength >= 2 && comboboxOpen
              return (
                <FormItem>
                  <FormControl>
                    <Combobox
                      openOnInputClick={(field.value ?? '').length >= 2}
                      items={skuOptions}
                      itemToStringValue={(option) => option.label}
                      value={selectedOption}
                      inputValue={field.value ?? ''}
                      open={open}
                      onOpenChange={setComboboxOpen}
                      onValueChange={(option) => {
                        if (option) {
                          applyOption(option)
                        }
                      }}
                    >
                      <ComboboxInput
                        data-item-description={index}
                        aria-label="Popis položky"
                        showTrigger={false}
                        className={cn('h-8 w-full', {
                          'border-destructive': !!rowErrors?.description,
                        })}
                        placeholder="Popis položky"
                        value={field.value ?? ''}
                        onChange={(e) => {
                          const v = e.target.value
                          field.onChange(v)
                          setValue(`items.${index}.skuId`, null)
                          if (v.trim().length >= 2) {
                            setComboboxOpen(true)
                          } else {
                            setComboboxOpen(false)
                          }
                        }}
                      />
                      {skuOptions.length > 0 && <ComboboxContent className="has-[[data-slot=combobox-list][data-empty]]:hidden">
                        <ComboboxList>
                          {(option) => (
                            <ComboboxItem
                              key={option.key}
                              value={option}
                            >
                              {option.label}
                            </ComboboxItem>
                          )}
                        </ComboboxList>
                      </ComboboxContent>}
                    </Combobox>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )
            }}
          />
        </div>

        <input type="hidden" {...register(`items.${index}.skuId`)} />

        <div className="min-w-0">
          <FormField
            control={control}
            name={`items.${index}.amount`}
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <CustomNumberInput
                    inputMode="decimal"
                    aria-label="Množství"
                    disabled={field.disabled}
                    className={cn('px-2 text-right tabular', {
                      'border-destructive': !!rowErrors?.amount,
                    })}
                    allowedDecimalSeparators={[',', '.']}
                    decimalScale={4}
                    allowNegative={false}
                    value={field.value ?? ''}
                    onValueChange={({ floatValue }) => {
                      field.onChange(floatValue ?? null)
                    }}
                    onBlur={field.onBlur}
                    name={field.name}
                    ref={field.ref}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>

        <div className="min-w-0">
          <FormField
            control={control}
            name={`items.${index}.unit`}
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input
                    className={cn('w-full px-2', {
                      'border-destructive': !!rowErrors?.unit,
                    })}
                    placeholder="ks"
                    aria-label="Měrná jednotka"
                    list="units"
                    {...field}
                  />
                </FormControl>
                <datalist id="units">
                  <option value="h">h</option>
                  <option value="ks">ks</option>
                  <option value="x">&times;</option>
                </datalist>
              </FormItem>
            )}
          />
        </div>

        <div>
          <Tooltip>
            <TooltipTrigger asChild>
              <FormField
                control={control}
                name={`items.${index}.unitPrice`}
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <div className="relative w-full">
                        <CustomNumberInput
                          inputMode="decimal"
                          aria-label="Cena za MJ"
                          disabled={field.disabled}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && onLastInputEnterKeyDown) {
                              e.preventDefault()
                              onLastInputEnterKeyDown()
                            }
                          }}
                          className={cn('pr-8 text-right tabular', {
                            'border-destructive': !!rowErrors?.unitPrice,
                          })}
                          value={field.value ?? ''}
                          onValueChange={({ floatValue }) => {
                            field.onChange(floatValue ?? null)
                          }}
                          onBlur={field.onBlur}
                          name={field.name}
                          ref={field.ref}
                        />
                        <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                          {currencySymbol}
                        </span>
                      </div>
                    </FormControl>

                    <FormMessage />
                  </FormItem>
                )}
              />
            </TooltipTrigger>
            {unitPriceError?.message && (
              <TooltipContent>
                <p>{t(String(unitPriceError.message))}</p>
              </TooltipContent>
            )}
          </Tooltip>
        </div>

        {isVatPayer && (
          <div className="min-w-0 [&_button]:w-full [&_button]:px-2.5">
            <TaxSelect
              key={field.id}
              control={control}
              name={`items.${index}.taxRate`}
            />
          </div>
        )}

        <div className="flex h-8 items-center justify-end">
          <Money amount={lineTotal} minor={false} currency={currencyCode} className={cn(lineTotal === 0 && 'text-muted-foreground')} />
        </div>

        <div className="flex h-8 items-center justify-end">
          {hasRemoveButton && (
            <Tooltip open={confirmRemove ? true : undefined}>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost-destructive"
                  size="icon-sm"
                  className={cn(confirmRemove && 'bg-danger-bg text-danger')}
                  aria-label={confirmRemove ? 'Opravdu odebrat?' : 'Odebrat položku'}
                  onClick={() => {
                    if (confirmRemove) {
                      onRemoveClick?.()
                    } else {
                      setConfirmRemove(true)

                      setTimeout(() => {
                        setConfirmRemove(false)
                      }, 3000)
                    }
                  }}
                >
                  <Trash2Icon className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                {confirmRemove ? 'Klikněte znovu pro odebrání' : 'Odebrat položku'}
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      </div>
    )
  },
)

DocumentItem.displayName = 'DocumentItem'
